import React, { useCallback } from "react";
import { LevelInfoLabel } from "@sscs/report/ui";
import { CheckLabel, Table } from "@sscs/ui";
import {
  CloseIcon as EnabledFalse,
  CheckIcon as EnabledTrue,
} from "@sscs/ui/icons";
import { getClassName } from "@sscs/utils";
import { AllowedTo } from "Components";
import { Permission } from "Constants";
import { useAppDispatch, useDialog } from "Hooks";
import {
  useDerivedPolicyOverride,
  useDerivedScanLevel,
  useOverrideWarning,
} from "../../hooks";
import {
  removeOverride,
  removeRevertOverride,
} from "../../store/policyConfigSlice";
import { ActionTypes, Policy } from "../../types";
import EditPolicy from "../EditPolicy/EditPolicy";
import OverrideRow from "../OverrideRow/OverrideRow";
import RevertPolicy from "../RevertPolicy/RevertPolicy";
import styles from "./policyRow.module.scss";
import RowActions from "./RowActions";

type PolicyRowProps = {
  policy: Policy;
  permissions: Permission[];
  groupId?: string;
  className?: string;
};

const PolicyRow = ({
  policy,
  permissions,
  groupId,
  className,
}: PolicyRowProps): React.JSX.Element => {
  const dispatch = useAppDispatch();

  const { derivedScanLevel, customizedPolicies } = useDerivedScanLevel();

  const {
    hasAnyOverride,
    hasUnsavedRevert,
    hasUnsavedOverride,
    policyOverrideState,
  } = useDerivedPolicyOverride({
    policy,
  });

  const { overrideWarning } = useOverrideWarning({ policy });

  const editDialog = useDialog();
  const revertDialog = useDialog();

  const handleActionClick = useCallback(
    (actionType: ActionTypes) => {
      switch (actionType) {
        case "EDIT_POLICY":
          editDialog.open();
          break;
        case "REVERT_POLICY":
          revertDialog.open();
          break;
        case "UNDO_REVERT":
          dispatch(removeRevertOverride(policy.id));
          break;
        case "UNDO_OVERRIDE":
          dispatch(removeOverride(policy.id));
          break;
        default:
          return;
      }
    },
    [dispatch, editDialog, policy.id, revertDialog],
  );

  return (
    <>
      <Table.Row
        hover
        className={getClassName(styles["policy-row"], className, {
          [styles["has-override"]]: hasAnyOverride,
        })}
      >
        <Table.Cell>{policy.id}</Table.Cell>
        <Table.Cell>{policy.description}</Table.Cell>
        <Table.Cell align="center">
          <div className={styles["status-column"]}>
            {policyOverrideState.enabled ? <EnabledTrue /> : <EnabledFalse />}
          </div>
        </Table.Cell>
        <Table.Cell align="center">
          <div className={styles["status-column"]}>
            <CheckLabel
              status={policyOverrideState.blocker ? "fail" : "pass"}
              scanLevel={derivedScanLevel ?? 0}
              isLevelCustomized={customizedPolicies.length > 0}
            />
            <LevelInfoLabel
              policyLevel={policy.rl_level}
              status={policyOverrideState.blocker ? "fail" : "pass"}
              enabled={policyOverrideState.enabled}
              scanLevel={derivedScanLevel ?? 0}
            />
          </div>
        </Table.Cell>
        <AllowedTo permissions={permissions} groupId={groupId}>
          <Table.Cell align="center">
            <div className={styles.actions}>
              <RowActions policy={policy} onActionClick={handleActionClick} />
            </div>
          </Table.Cell>
        </AllowedTo>
      </Table.Row>
      <OverrideRow
        overrideWarning={overrideWarning}
        overrideState={policyOverrideState}
        hasAnyOverride={hasAnyOverride}
        hasUnsavedRevert={hasUnsavedRevert}
        hasUnsavedOverride={hasUnsavedOverride}
        className={className}
      />
      <AllowedTo permissions={permissions} groupId={groupId}>
        <EditPolicy
          isOpen={editDialog.isOpen}
          onClose={editDialog.close}
          policy={policy}
          policyOverrideState={policyOverrideState}
          derivedScanLevel={derivedScanLevel ?? 0}
        />
        <RevertPolicy
          isOpen={revertDialog.isOpen}
          onClose={revertDialog.close}
          policy={policy}
        />
      </AllowedTo>
    </>
  );
};

export default PolicyRow;
